import { useEffect, useState } from "react";
import { DraftingCompass, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { preconApi } from "../lib/api";
import { ApprovedEqualDialog } from "@/budget/components/dashboard/ApprovedEqualDialog";

/** BOD-1 §5 — Y/N toggle on the bid. Y → popup: the bid's scopes (else Ops Codes) with the ABS manufacturer per scope,
 *  architect prefilled from the directory, checkboxes → one BOD row per scope (Type P). N archives, reversible. */
export default function BodToggle({ job, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const [picked, setPicked] = useState({});
  const [arch, setArch] = useState({ architect: "", architect_contact_name: "", architect_contact_email: "", architect_contact_phone: "" });
  const [emailRow, setEmailRow] = useState(null);
  const on = !!job.bod_enabled;
  const mode = on ? (job.bod_mode === "AE" ? "AE" : "Y") : "N";
  const [pickMode, setPickMode] = useState(job.bod_mode === "AE" ? "AE" : "Y");

  const load = async () => {
    const o = await preconApi.bodOptions(job.id);
    setOpts(o);
    setPicked(Object.fromEntries(o.scopes.map((s) => [s.scope_code, s.tracked || o.scopes.length <= 3])));
    const c = o.architect_contact || {};
    const row = (o.rows || [])[0] || {};
    setArch({ architect: o.architect || "", architect_contact_name: row.architect_contact_name || c.name || "",
              architect_contact_email: row.architect_contact_email || c.email || "", architect_contact_phone: row.architect_contact_phone || c.phone || "" });
  };
  useEffect(() => { if (open) load().catch(() => toast.error("Could not load the scopes")); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (next) => {
    if (!canEdit && !next) return;
    if (next) { setOpen(true); return; }
    if (!window.confirm("Set BOD to N? Its Basis of Design rows are archived (status kept) and come back if you set Y again.")) return;
    try { const r = await preconApi.setBod(job.id, { on: false }); toast.success(`BOD off — ${r.archived_rows} row(s) archived`); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openTab = () => window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "bod" } }));
  const chosen = () => (opts?.scopes || []).filter((s) => picked[s.scope_code]);
  const confirm = async () => {
    if (!chosen().length) { toast.error("Pick at least one scope"); return; }
    try {
      const r = await preconApi.setBod(job.id, { on: true, scopes: chosen(), mode: pickMode, ...arch });
      toast.success(`Tracking ${r.created.length + r.restored.length} scope(s) — ${pickMode === "AE" ? "Approved Equal" : "Basis of Design"}`);
      setOpen(false); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const requestApproval = async () => {
    if (!chosen().length) { toast.error("Pick the scopes first"); return; }
    if (!arch.architect_contact_email.trim()) { toast.error("Add the architect contact email first"); return; }
    try {
      await preconApi.setBod(job.id, { on: true, scopes: chosen(), mode: pickMode, ...arch });
      onChanged?.();
      const first = chosen()[0];
      setEmailRow({ budget_id: `bid:${job.id}`, scope_code: first.scope_code, project_name: job.project_name, architect_contact_email: arch.architect_contact_email });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <button type="button" disabled={!canEdit && !on} onClick={() => setOpen(true)} data-testid={`bod-toggle-${job.id}`} data-on={mode}
              title={mode === "AE" ? "Approved Equal — open to see or change the scopes" : on ? "Basis of Design — open to see or change the scopes" : "BOD off — click to pick scopes"}
              className={`rounded px-2 py-0.5 text-[10px] font-semibold text-black ${mode === "AE" ? "bg-emerald-200" : on ? "bg-green-300" : "bg-red-500"} disabled:cursor-not-allowed`}>{mode}</button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl overflow-hidden" data-testid="bod-popup" onClick={(e) => e.stopPropagation()}>
          <DialogHeader><DialogTitle className="text-base">Basis of Design — {job.project_name}</DialogTitle></DialogHeader>
          {!opts ? <div className="text-xs text-muted-foreground">loading…</div> : (
            <div className="space-y-3 text-xs">
              <div className="text-muted-foreground">Scopes from {opts.scope_source}; manufacturer = the ABS manufacturer on Budgets › Manufacturers.</div>
              <div className="max-h-56 space-y-0.5 overflow-y-auto overflow-x-hidden rounded border border-border p-1.5" data-testid="bod-popup-scopes">
                {opts.scopes.map((s) => (
                  <label key={s.scope_code} className="flex min-w-0 items-center gap-2 rounded px-1 py-0.5 hover:bg-muted/60" data-testid={`bod-scope-${s.scope_code}`}>
                    <input type="checkbox" className="shrink-0" checked={!!picked[s.scope_code]} onChange={(e) => setPicked({ ...picked, [s.scope_code]: e.target.checked })} data-testid={`bod-scope-check-${s.scope_code}`} />
                    <span className="shrink-0 font-mono">{s.scope_code}</span>{s.scope_alias && <b className="shrink-0">{s.scope_alias}</b>}
                    <span className="min-w-0 flex-1 truncate">{s.scope_name}</span>
                    <span className="ml-auto max-w-[45%] shrink-0 truncate text-right text-muted-foreground" title={(s.manufacturers || []).join(", ")} data-testid={`bod-scope-manu-${s.scope_code}`}>{(s.manufacturers || []).join(", ") || "— no manufacturer"}</span>
                  </label>))}
              </div>
              <div className="flex items-center gap-3" data-testid="bod-mode">
                <span className="text-muted-foreground">We are the</span>
                {[["Y", "Basis of Design (Y)"], ["AE", "Approved Equal (AE)"]].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-1">
                    <input type="radio" name={`bod-mode-${job.id}`} checked={pickMode === k} onChange={() => setPickMode(k)} data-testid={`bod-mode-${k}`} />{l}
                  </label>))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Architect firm" value={arch.architect} onChange={(e) => setArch({ ...arch, architect: e.target.value })} className="h-8 text-xs" data-testid="bod-architect" />
                <Input placeholder="Architect contact" value={arch.architect_contact_name} onChange={(e) => setArch({ ...arch, architect_contact_name: e.target.value })} className="h-8 text-xs" data-testid="bod-architect-name" />
                <Input placeholder="Contact email" value={arch.architect_contact_email} onChange={(e) => setArch({ ...arch, architect_contact_email: e.target.value })} className="h-8 text-xs" data-testid="bod-architect-email" />
                <Input placeholder="Contact phone" value={arch.architect_contact_phone} onChange={(e) => setArch({ ...arch, architect_contact_phone: e.target.value })} className="h-8 text-xs" data-testid="bod-architect-phone" />
              </div>
            </div>)}
          <DialogFooter className="flex-wrap gap-1 sm:justify-end">
            {on && canEdit && <Button variant="ghost" size="sm" className="text-red-600" onClick={() => { setOpen(false); toggle(false); }} data-testid="bod-popup-set-n">Set to N</Button>}
            {on && <Button variant="ghost" size="sm" className="mr-auto" onClick={() => { setOpen(false); openTab(); }} data-testid="bod-popup-open-tab"><DraftingCompass className="mr-1 h-3 w-3" /> Open Basis of Design</Button>}
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="bod-popup-cancel">Cancel</Button>
            <Button variant="outline" size="sm" onClick={requestApproval} data-testid="bod-popup-request" title="Bundled Request Approval email — review before send"><Mail className="mr-1 h-3 w-3" /> Request Approval</Button>
            <Button size="sm" onClick={confirm} data-testid="bod-popup-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {emailRow && <ApprovedEqualDialog row={emailRow} onClose={(sent) => { setEmailRow(null); setOpen(false); if (sent) onChanged?.(); }} />}
    </>
  );
}
